import { useEffect, useRef } from 'react';
import { PHONE_1, PHONE_2, PHONE_1_DISPLAY, PHONE_2_DISPLAY } from '../../../components/feature/TopBar';

const WA_LINK = `https://wa.me/90${PHONE_1}?text=${encodeURIComponent('Merhaba, profesyonel temizlik hizmeti hakkında bilgi almak istiyorum.')}`;

const badges = [
  { icon: 'ri-award-line', label: '18 Yıl Tecrübe' },
  { icon: 'ri-tools-line', label: 'Alman Karcher' },
  { icon: 'ri-home-smile-line', label: 'Evde Hizmet' },
  { icon: 'ri-price-tag-3-line', label: 'Kampanya Fiyatı' },
];

export default function HeroSection() {
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0');
      },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const handleScroll = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-[100svh] flex items-center overflow-hidden">
      {/* Background Image */}
      <img
        src="https://readdy.ai/api/search-image?query=professional%20upholstery%20cleaning%20service%20technician%20using%20karcher%20steam%20extraction%20machine%20on%20grey%20sofa%20in%20modern%20bright%20living%20room%20clean%20white%20background%20high%20quality%20realistic%20photo&width=1440&height=900&seq=hero01&orientation=landscape"
        alt="Profesyonel Koltuk Temizliği"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/65 to-black/30" />

      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 py-20 md:py-28">
        <div
          ref={scrollRef}
          className="opacity-0 translate-y-8 transition-all duration-700 max-w-3xl"
        >
          {/* Badges */}
          <div className="flex flex-wrap gap-2 mb-8">
            {badges.map((b) => (
              <span
                key={b.label}
                className="flex items-center gap-1.5 bg-white/10 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${b.icon}`} />
                </span>
                {b.label}
              </span>
            ))}
          </div>

          {/* Headline */}
          <h1
            className="text-4xl md:text-6xl lg:text-7xl font-extrabold text-white leading-tight mb-5"
            style={{ fontFamily: 'Poppins, sans-serif' }}
          >
            Evinizde
            <br />
            <span className="text-emerald-400">Profesyonel</span>
            <br />
            Temizlik Hizmeti
          </h1>

          {/* Sub */}
          <p className="text-emerald-400 text-lg md:text-xl font-semibold mb-4 tracking-wide">
            Uygun Fiyat &bull; Gerçek Sonuç &bull; 18 Yıllık Tecrübe
          </p>

          {/* Description */}
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-8 max-w-2xl">
            Koltuk, yatak, sandalye yıkama ve petek temizliğinde profesyonel
            ekipman ve doğru yöntemlerle hizmet veriyoruz. Anadolu Yakası ve
            Avrupa Yakası genelinde evde hizmet.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <a
              href={WA_LINK}
              target="_blank"
              rel="nofollow noreferrer"
              className="flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-white font-bold text-base px-8 py-4 rounded-xl transition-all hover:scale-105 shadow-lg shadow-emerald-500/30 whitespace-nowrap"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-lg" />
              </span>
              Hemen Randevu Al
            </a>
            <button
              onClick={() => handleScroll('#services')}
              className="flex items-center justify-center gap-2 border-2 border-white/70 text-white hover:bg-white hover:text-[#1a1a1a] font-bold text-base px-8 py-4 rounded-xl transition-all whitespace-nowrap cursor-pointer"
            >
              <span className="w-5 h-5 flex items-center justify-center">
                <i className="ri-eye-line" />
              </span>
              Hizmetleri İncele
            </button>
          </div>

          {/* Phone Numbers */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
            <a
              href={`tel:${PHONE_1}`}
              className="flex items-center gap-2 text-white hover:text-emerald-400 font-semibold text-xl transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 rounded-full">
                <i className="ri-phone-fill text-emerald-400 text-sm" />
              </span>
              {PHONE_1_DISPLAY}
            </a>
            <a
              href={`tel:${PHONE_2}`}
              className="flex items-center gap-2 text-white hover:text-emerald-400 font-semibold text-xl transition-colors whitespace-nowrap"
            >
              <span className="w-6 h-6 flex items-center justify-center bg-emerald-500/20 rounded-full">
                <i className="ri-phone-fill text-emerald-400 text-sm" />
              </span>
              {PHONE_2_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/50 animate-bounce">
        <span className="w-6 h-6 flex items-center justify-center">
          <i className="ri-arrow-down-line text-xl" />
        </span>
      </div>
    </section>
  );
}
